// Phase C: Promo Codes — Frontend React Components
// Add these to App.js or create as separate component files

import React, { useState, useCallback } from 'react';

// ============================================================================
// COMPONENT 1: Promo Code Modal
// ============================================================================
function PromoCodeModal({ isOpen, onClose, onSuccess, deviceId, apiUrl = '/api' }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/redeem-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code.trim().toUpperCase(),
          device_id: deviceId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || data.error || 'Failed to redeem code');
        return;
      }

      // Success
      setSuccess(true);
      setCode('');
      
      // Call parent callback to update UI
      if (onSuccess) {
        onSuccess(data);
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Promo code error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-bold mb-4">Enter Promo Code</h2>

        {success ? (
          <div className="text-center">
            <div className="text-green-600 text-4xl mb-2">✓</div>
            <p className="text-green-600 font-semibold">
              You now have Pro access!
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Enter code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
              autoFocus
            />

            {error && (
              <p className="text-red-600 text-sm mb-4">{error}</p>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !code.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Redeeming...' : 'Redeem'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENT 2: Pro Badge (shows usage or Pro status)
// ============================================================================
// searchesLeft is now only passed for PayG (Trial Pack) users — it carries
// their remaining credit count e.g. "12 credits".
// Free users pass null (comparisons are unlimited — Step 1 of strategy).
// The yellow "searches left" badge is removed entirely for free users.
function ProBadge({ isProViaPaid, isProViaCode, proUntilDate, searchesLeft }) {
  if (isProViaPaid) {
    return (
      <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
        Pro (Stripe)
      </div>
    );
  }

  if (isProViaCode) {
    const daysLeft = proUntilDate
      ? Math.ceil(
          (new Date(proUntilDate) - new Date()) / (1000 * 60 * 60 * 24)
        )
      : 0;
    return (
      <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
        Pro access ({daysLeft} days left)
      </div>
    );
  }

  // PayG (Trial Pack) users: show remaining credits
  if (searchesLeft != null) {
    return (
      <div className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
        {searchesLeft}
      </div>
    );
  }

  // Free tier: no badge — comparisons are unlimited, nothing to show
  return null;
}

// ============================================================================
// COMPONENT 3: Upgrade Modal (with promo code option)
// ============================================================================
function UpgradeModal({ isOpen, onClose, onPromoClick, isPromoModalOpen, onPromoSuccess, deviceId }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 w-full max-w-md">
        <h2 className="text-2xl font-bold mb-2">Upgrade to Pro</h2>
        <p className="text-gray-600 mb-6">Unlock unlimited searches & basket splitting</p>

        <div className="space-y-2 mb-6">
          <div className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            <span>Unlimited searches</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            <span>Basket splitting</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            <span>Price alerts (coming soon)</span>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-3xl font-bold mb-1">£2.99</p>
          <p className="text-gray-600 text-sm">/month, cancel anytime</p>
        </div>

        <button
          onClick={() => {
            // Navigate to Stripe checkout
            window.location.href = '/checkout';
          }}
          className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold mb-4"
        >
          Upgrade to Pro
        </button>

        <div className="relative mb-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">or</span>
          </div>
        </div>

        <button
          onClick={onPromoClick}
          className="w-full px-4 py-2 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 font-semibold"
        >
          Have a code?
        </button>

        <button
          onClick={onClose}
          className="w-full mt-4 px-4 py-2 text-gray-700 hover:bg-gray-50"
        >
          Close
        </button>

        {/* Embed promo code modal */}
        <PromoCodeModal
          isOpen={isPromoModalOpen}
          onClose={onClose}
          onSuccess={onPromoSuccess}
          deviceId={deviceId}
        />
      </div>
    </div>
  );
}

// ============================================================================
// INTEGRATION: Add to main App.js component state
// ============================================================================
// Add these state variables to your App() component:

/*
const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
const [proUntilDate, setProUntilDate] = useState(null);
const [isProViaCode, setIsProViaCode] = useState(false);

// Load user's pro status on mount
useEffect(() => {
  const loadProStatus = async () => {
    try {
      const response = await fetch(`/api/usage?device_id=${deviceId}`);
      const data = await response.json();
      
      // Check if has pro_until from promo code
      if (data.pro_until) {
        const proUntil = new Date(data.pro_until);
        if (proUntil > new Date()) {
          setProUntilDate(data.pro_until);
          setIsProViaCode(true);
        }
      }
      
      setIsProViaPaid(data.is_pro); // existing Stripe check
    } catch (err) {
      console.error('Error loading pro status:', err);
    }
  };
  
  if (deviceId) {
    loadProStatus();
  }
}, [deviceId]);

// Handle promo code success
const handlePromoSuccess = (data) => {
  setProUntilDate(data.pro_until);
  setIsProViaCode(true);
  
  // Show success toast
  showToast(`✓ ${data.message}`, 'success');
  
  // Close modals
  setIsUpgradeModalOpen(false);
  setIsPromoModalOpen(false);
};
*/

// ============================================================================
// INTEGRATION: Replace upgrade button in main UI
// ============================================================================
// Find where you show the Pro badge and upgrade button:

/*
// OLD CODE:
<button onClick={() => setIsUpgradeModalOpen(true)}>
  Upgrade to Pro
</button>

// NEW CODE:
<>
  <ProBadge
    isProViaPaid={isProViaPaid}
    isProViaCode={isProViaCode}
    proUntilDate={proUntilDate}
    searchesLeft={searchesLeft}
  />
  {!isProViaPaid && !isProViaCode && (
    <button
      onClick={() => setIsUpgradeModalOpen(true)}
      className="ml-2 px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
    >
      Upgrade
    </button>
  )}
</>

// And render the modal:
<UpgradeModal
  isOpen={isUpgradeModalOpen}
  onClose={() => {
    setIsUpgradeModalOpen(false);
    setIsPromoModalOpen(false);
  }}
  onPromoClick={() => setIsPromoModalOpen(true)}
  isPromoModalOpen={isPromoModalOpen}
  onPromoSuccess={handlePromoSuccess}
  deviceId={deviceId}
/>
*/

// ============================================================================
// COMPONENT 4: Admin Panel (Optional - for managing codes)
// ============================================================================
function PromoCodesAdminPanel() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);

  const loadCodes = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(
        `/api/admin/promo-codes?active_only=${activeOnly}`,
        {
          headers: {
            'X-Admin-Key': process.env.REACT_APP_ADMIN_KEY,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load codes');
      }

      const data = await response.json();
      setCodes(data.codes || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  return (
    <div className="p-6 bg-white rounded-lg">
      <h2 className="text-xl font-bold mb-4">Promo Codes Manager</h2>

      <div className="mb-4 flex items-center gap-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="mr-2"
          />
          <span>Active only</span>
        </label>
        <button
          onClick={loadCodes}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && <p className="text-red-600 mb-4">{error}</p>}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2">Code</th>
              <th className="text-left py-2">Usage</th>
              <th className="text-left py-2">Expires</th>
              <th className="text-left py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => (
              <tr key={code.code} className="border-b hover:bg-gray-50">
                <td className="py-2 font-mono">{code.code}</td>
                <td className="py-2">
                  {code.times_used}/{code.max_uses} ({code.usage_pct}%)
                </td>
                <td className="py-2 text-sm text-gray-600">
                  {code.expires_at
                    ? new Date(code.expires_at).toLocaleDateString()
                    : 'Never'}
                </td>
                <td className="py-2">
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      code.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : code.status === 'expired'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {code.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export { PromoCodeModal, ProBadge, UpgradeModal, PromoCodesAdminPanel };
